import { cx } from '@emotion/css';
import { ButtonType, MotifScheme } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { CallToAction } from '../CallToAction';
import type { CallToActionDataProps } from '../CallToAction/types';
import { persistentCtaContainerCss, persistentCtaVisibleCss } from './PersistentCta.styles';

interface PersistentCtaProps {
  mobileCta: CallToActionDataProps;
  shouldScrollToShow?: boolean;
}

export const PersistentCta: FC<PersistentCtaProps> = ({ mobileCta, shouldScrollToShow }) => {
  const [isVisible, setIsVisible] = useState(!shouldScrollToShow);

  const button = useMemo(
    () =>
      mobileCta.presentation
        ? { ...mobileCta.presentation, buttonType: ButtonType.Primary }
        : undefined,
    [mobileCta.presentation]
  );

  useEffect(() => {
    if (!button || !shouldScrollToShow) return;

    const handleScroll = () => {
      const shouldShow = window.scrollY >= window.innerHeight;
      setIsVisible(shouldShow);
    };

    document.addEventListener('scroll', handleScroll, { passive: true });

    handleScroll();

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [button, shouldScrollToShow]);

  if (!button) return null;

  return (
    <aside
      data-testid="mwp-persistent-cta"
      className={cx(MotifScheme.SECONDARY, persistentCtaContainerCss, {
        [persistentCtaVisibleCss]: isVisible,
      })}
      aria-hidden={!isVisible}
    >
      <CallToAction {...mobileCta} presentation={button} />
    </aside>
  );
};

PersistentCta.displayName = 'PersistentCta';
